import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Printer, FileText, DollarSign, Eye, ChevronUp, Upload, Trash2, Plus } from "lucide-react";
import { downloadCotizacionPdf, printCotizacionPdf } from "@/lib/pdf";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const COT_LOGO = "/logo-cooper.png";
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const m = (v) => "$" + Number(v || 0).toFixed(2);

const FICHAS = [
  { key: "rectangulo", nombre: "Modelo Rectángulo", desc: "Medidas (cm) de las variantes rectangulares.", url: "/fichas/rectangulo.pdf", img: "/fichas/rectangulo.png" },
  { key: "cuadrado", nombre: "Modelo Cuadrado", desc: "Medidas (cm) de las variantes cuadradas.", url: "/fichas/cuadrado.pdf", img: "/fichas/cuadrado.png" },
  { key: "mixto", nombre: "Modelo Mixto", desc: "Medidas (cm) de las variantes mixtas.", url: "/fichas/mixto.pdf", img: "/fichas/mixto.png" },
  { key: "duelamax", nombre: "DuelaMAX", desc: "Medidas (cm) de la línea DuelaMAX.", url: "/fichas/duelamax.pdf", img: "/fichas/duelamax.png" },
];

export default function Data() {
  const [listas, setListas] = useState([]);
  const [selId, setSelId] = useState("");
  const [items, setItems] = useState([]);
  const [abierto, setAbierto] = useState(null); // "precios" | ficha.key | null
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const { isAdmin } = useAuth();
  const [uploaded, setUploaded] = useState([]);
  const [prevUrls, setPrevUrls] = useState({});
  const [upOpen, setUpOpen] = useState(false);
  const [upNombre, setUpNombre] = useState("");
  const [uploading, setUploading] = useState(false);
  const upFileRef = useRef(null);

  const fetchFichas = () => api.get("/documentos", { params: { entidad_tipo: "ficha", entidad_id: "global" } }).then(({ data }) => setUploaded(data || [])).catch(() => {});
  useEffect(() => { fetchFichas(); }, []);

  const verUploaded = async (doc) => {
    setAbierto(doc.id);
    if (!prevUrls[doc.id]) {
      try {
        const { data } = await api.get(`/documentos/${doc.id}/download`, { params: { preview: true }, responseType: "blob" });
        setPrevUrls((p) => ({ ...p, [doc.id]: URL.createObjectURL(data) }));
      } catch (e) { toast.error("No se pudo cargar la vista previa."); }
    }
  };
  const blobFicha = async (doc, descargar) => {
    try {
      const { data } = await api.get(`/documentos/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (descargar) { const a = document.createElement("a"); a.href = url; a.download = doc.nombre_archivo; document.body.appendChild(a); a.click(); a.remove(); }
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("No se pudo abrir el documento."); }
  };
  const subirFicha = async () => {
    const file = upFileRef.current?.files?.[0];
    if (!upNombre.trim()) return toast.error("Escribe el nombre de la ficha.");
    if (!file) return toast.error("Selecciona un archivo PDF.");
    if (file.type !== "application/pdf") return toast.error("La ficha debe ser un PDF.");
    if (file.size > 15 * 1024 * 1024) return toast.error("El archivo supera 15 MB.");
    const fd = new FormData();
    fd.append("entidad_tipo", "ficha"); fd.append("entidad_id", "global");
    fd.append("tipo_documento", upNombre.trim()); fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/documentos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Ficha agregada.");
      setUpOpen(false); setUpNombre(""); if (upFileRef.current) upFileRef.current.value = "";
      fetchFichas();
    } catch (e) { toast.error(`Error: ${e?.response?.data?.detail || e.message}`); }
    finally { setUploading(false); }
  };
  const eliminarFicha = async (doc) => {
    if (!window.confirm("¿Eliminar esta ficha?")) return;
    try { await api.delete(`/documentos/${doc.id}`); toast.success("Ficha eliminada."); if (abierto === doc.id) setAbierto(null); fetchFichas(); }
    catch (e) { toast.error(`Error: ${e?.response?.data?.detail || e.message}`); }
  };

  useEffect(() => {
    api.get("/price-lists").then(({ data }) => {
      setListas(data || []);
      if ((data || []).length) setSelId(data[0].id);
    });
  }, []);
  useEffect(() => {
    if (!selId) { setItems([]); return; }
    api.get(`/price-lists/${selId}/products`).then(({ data }) => setItems(data || []));
  }, [selId]);

  const selLista = listas.find((l) => l.id === selId);
  const listaName = selLista?.name || "";
  const now = new Date();
  const periodo = `${MESES[now.getMonth()]} ${now.getFullYear()}`;
  const periodoCorto = `${MESES[now.getMonth()]} ${String(now.getFullYear()).slice(-2)}`;
  const fileName = `${listaName || "Lista de precios"} ${periodoCorto}`;
  const handleDownload = () => downloadCotizacionPdf(bodyRef.current, headerRef.current, fileName);
  const handlePrint = () => printCotizacionPdf(bodyRef.current, headerRef.current);

  const descargarFicha = (url, nombre) => {
    const a = document.createElement("a");
    a.href = url; a.download = `Ficha tecnica - ${nombre}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const imprimirFicha = (url) => {
    const ifr = document.createElement("iframe");
    ifr.style.display = "none"; ifr.src = url;
    ifr.onload = () => { try { ifr.contentWindow.focus(); ifr.contentWindow.print(); } catch (e) { window.open(url, "_blank"); } };
    document.body.appendChild(ifr);
  };
  const toggle = (key) => setAbierto((prev) => (prev === key ? null : key));
  const navigate = useNavigate();

  const fichaAbierta = FICHAS.find((f) => f.key === abierto);

  // Lista dividida en 2 hojas (igual que el PDF)
  const half = Math.ceil(items.length / 2);
  const pages = items.length ? [items.slice(0, half), items.slice(half)] : [];

  const Membrete = () => (
    <div className="px-8 pt-6">
      <div className="grid grid-cols-[auto_1fr_auto] gap-6 items-start bg-gray-50 px-3 py-3">
        <div className="text-center">
          <img src={COT_LOGO} className="h-16 w-16 object-contain rounded-lg mx-auto" alt="Cooper Slip" />
          <div className="text-xs mt-1">Cooper Slip</div>
        </div>
        <div className="text-center text-sm font-bold leading-snug pt-3">
          <p>Cuando tu cliente queda bien, Tu quedas mejor.</p>
          <p>Elije las duelas que respaldan a tu negocio</p>
        </div>
        <div className="text-right">
          <h2 className="text-3xl font-extrabold tracking-tight">LISTA DE PRECIOS</h2>
          {listaName && <p className="text-sm mt-1"><span className="font-bold">Lista:</span> {listaName}</p>}
          <p className="text-sm"><span className="font-bold">Vigencia:</span> {periodo}</p>
        </div>
      </div>
      <div className="border-t-4 border-black" />
    </div>
  );

  const IntroP = () => (
    <p className="text-sm mb-4 leading-relaxed avoid-break">
      Estimado cliente, es un gusto saludarle. Nos complace presentarle nuestra lista de precios vigente de <span className="font-semibold">Duelas Cooper Slip</span>, la mejor opción en duelas para la fabricación de puertas y portones. Cada modelo está elaborado con materiales de la más alta calidad para respaldar el prestigio de su negocio. Quedamos a sus órdenes para cualquier pedido o aclaración.
    </p>
  );

  const PriceRows = ({ rows }) => (
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr className="bg-gray-100">
          <th className="border border-gray-300 px-3 py-1 text-left w-28">N/REF</th>
          <th className="border border-gray-300 px-3 py-1 text-left">Modelo</th>
          <th className="border border-gray-300 px-3 py-1 text-right w-40">Precio Unitario</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((it) => (
          <tr key={it.id}>
            <td className="border border-gray-300 px-3 py-1 font-mono">{it.n_ref}</td>
            <td className="border border-gray-300 px-3 py-1">{it.modelo}</td>
            <td className="border border-gray-300 px-3 py-1 text-right font-semibold">{m(it.precio_unitario)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const Firma = () => (
    <>
      <p className="text-[10px] text-gray-500 mt-2 avoid-break">Precios en pesos mexicanos (MXN), antes de IVA. Sujetos a cambio sin previo aviso.</p>
      <div className="mt-8 mb-6 text-sm avoid-break" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <p className="font-semibold">Atentamente,</p>
        <p>Departamento de Ventas</p>
        <p className="text-xs text-gray-600">Duelas Cooper Slip</p>
      </div>
    </>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="data-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">DATA</h1>
        <p className="text-muted-foreground mt-2">Lista de precios y fichas técnicas para consultar, descargar e imprimir.</p>
      </div>

      {/* Tarjetas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Card Lista de precios */}
        <Card data-testid="data-card-precios" className={abierto === "precios" ? "ring-2 ring-primary" : ""}>
          <CardHeader className="flex flex-row items-center gap-3 pb-2">
            <div className="p-2 bg-primary/10 rounded-lg"><DollarSign className="h-5 w-5 text-primary" /></div>
            <CardTitle className="text-base">Lista de Precios</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Precios oficiales por modelo. Consulta, descarga o imprime.</p>
            <Button variant={abierto === "precios" ? "default" : "outline"} size="sm" onClick={() => toggle("precios")} data-testid="data-ver-precios">
              {abierto === "precios" ? <><ChevronUp className="h-4 w-4 mr-2" /> Ocultar</> : <><Eye className="h-4 w-4 mr-2" /> Ver</>}
            </Button>
          </CardContent>
        </Card>

        {/* Card Alta de cliente (formulario) */}
        <Card data-testid="data-card-alta" className="cursor-pointer hover:ring-2 hover:ring-primary transition" onClick={() => navigate("/solicitud-alta-cliente")}>
          <CardHeader className="flex flex-row items-center gap-3 pb-2">
            <div className="p-2 bg-primary/10 rounded-lg"><FileText className="h-5 w-5 text-primary" /></div>
            <CardTitle className="text-base">Alta de Cliente (Formulario)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Llena los datos del cliente y genera un PDF membretado para enviarlo a administración.</p>
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate("/solicitud-alta-cliente"); }} data-testid="data-abrir-alta"><FileText className="h-4 w-4 mr-2" /> Abrir formulario</Button>
          </CardContent>
        </Card>

        {/* Cards fichas */}
        {FICHAS.map((f) => (
          <Card key={f.key} data-testid={`data-card-${f.key}`} className={abierto === f.key ? "ring-2 ring-primary" : ""}>
            <CardHeader className="flex flex-row items-center gap-3 pb-2">
              <div className="p-2 bg-primary/10 rounded-lg"><FileText className="h-5 w-5 text-primary" /></div>
              <CardTitle className="text-base">{f.nombre}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">{f.desc}</p>
              <div className="flex flex-wrap gap-2">
                <Button variant={abierto === f.key ? "default" : "outline"} size="sm" onClick={() => toggle(f.key)} data-testid={`data-ver-${f.key}`}>
                  {abierto === f.key ? <><ChevronUp className="h-4 w-4 mr-2" /> Ocultar</> : <><Eye className="h-4 w-4 mr-2" /> Ver</>}
                </Button>
                <Button variant="outline" size="sm" onClick={() => descargarFicha(f.url, f.nombre)} data-testid={`data-ficha-descargar-${f.key}`}><Download className="h-4 w-4 mr-2" /> Descargar</Button>
                <Button variant="outline" size="sm" onClick={() => imprimirFicha(f.url)} data-testid={`data-ficha-imprimir-${f.key}`}><Printer className="h-4 w-4 mr-2" /> Imprimir</Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Fichas subidas (object storage) */}
        {uploaded.map((f) => (
          <Card key={f.id} data-testid={`data-card-up-${f.id}`} className={abierto === f.id ? "ring-2 ring-primary" : ""}>
            <CardHeader className="flex flex-row items-center gap-3 pb-2">
              <div className="p-2 bg-primary/10 rounded-lg"><FileText className="h-5 w-5 text-primary" /></div>
              <CardTitle className="text-base">{f.tipo_documento}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground truncate">{f.nombre_archivo}</p>
              <div className="flex flex-wrap gap-2">
                <Button variant={abierto === f.id ? "default" : "outline"} size="sm" onClick={() => (abierto === f.id ? setAbierto(null) : verUploaded(f))} data-testid={`data-ver-up-${f.id}`}>
                  {abierto === f.id ? <><ChevronUp className="h-4 w-4 mr-2" /> Ocultar</> : <><Eye className="h-4 w-4 mr-2" /> Ver</>}
                </Button>
                <Button variant="outline" size="sm" onClick={() => blobFicha(f, true)} data-testid={`data-desc-up-${f.id}`}><Download className="h-4 w-4 mr-2" /> Descargar</Button>
                <Button variant="outline" size="sm" onClick={() => blobFicha(f, false)} data-testid={`data-print-up-${f.id}`}><Printer className="h-4 w-4 mr-2" /> Imprimir</Button>
                {isAdmin && <Button variant="ghost" size="icon" className="text-destructive" title="Eliminar" onClick={() => eliminarFicha(f)} data-testid={`data-del-up-${f.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Tarjeta para subir nueva ficha (solo admin) */}
        {isAdmin && (
          <Card data-testid="data-card-subir-ficha" className="border-dashed">
            <CardHeader className="flex flex-row items-center gap-3 pb-2">
              <div className="p-2 bg-primary/10 rounded-lg"><Plus className="h-5 w-5 text-primary" /></div>
              <CardTitle className="text-base">Agregar ficha</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">Sube una nueva ficha técnica en PDF.</p>
              <Button variant="outline" size="sm" onClick={() => setUpOpen(true)} data-testid="data-subir-ficha-btn"><Upload className="h-4 w-4 mr-2" /> Subir ficha</Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Panel expandible: Lista de precios (vista previa fiel al PDF membretado) */}
      {abierto === "precios" && (
        <Card data-testid="data-panel-precios">
          <CardHeader className="gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-xl">Lista de Precios</CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={selId} onValueChange={setSelId}>
                  <SelectTrigger className="w-60" data-testid="data-lista-select"><SelectValue placeholder="Seleccione una lista" /></SelectTrigger>
                  <SelectContent>{listas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={handleDownload} data-testid="data-precios-descargar"><Download className="h-4 w-4 mr-2" /> Descargar PDF</Button>
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="data-precios-imprimir"><Printer className="h-4 w-4 mr-2" /> Imprimir</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {items.length === 0 ? (
              <div className="text-center h-20 flex items-center justify-center text-muted-foreground">Cargando...</div>
            ) : (
              <div className="bg-gray-200 rounded-md p-4 sm:p-8 overflow-x-auto flex flex-col items-center gap-6" data-testid="data-precios-preview">
                <style>{`.pdf-exporting table td,.pdf-exporting table th{line-height:1.4!important;padding-top:5px!important;padding-bottom:5px!important}.pdf-exporting tr{page-break-inside:avoid;break-inside:avoid}`}</style>
                {pages.map((rows, idx) => (
                  <div key={idx} data-testid={`data-precios-hoja-${idx + 1}`} className="bg-white text-black shadow-lg w-[750px] max-w-full box-border shrink-0 flex flex-col" style={{ minHeight: "970px" }}>
                    <Membrete />
                    <div className="px-8 pt-4 pb-6 flex-1">
                      {idx === 0 && <IntroP />}
                      <PriceRows rows={rows} />
                      {idx === pages.length - 1 && <Firma />}
                    </div>
                    <div className="px-8 pb-4 text-right text-xs text-gray-500">Hoja {idx + 1} de {pages.length}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bloque de exportación (fuera de pantalla) — 2 hojas con salto de página */}
      {abierto === "precios" && items.length > 0 && (
        <div style={{ position: "absolute", left: "-10000px", top: 0 }} aria-hidden="true">
          <div className="bg-white text-black w-[750px] box-border">
            <div ref={headerRef}><Membrete /></div>
            <div ref={bodyRef} className="px-8 pt-4 pb-16">
              <IntroP />
              <PriceRows rows={pages[0] || []} />
              <div style={{ pageBreakBefore: "always", breakBefore: "page" }} />
              <PriceRows rows={pages[1] || []} />
              <Firma />
            </div>
          </div>
        </div>
      )}

      {/* Panel expandible: Ficha técnica */}
      {fichaAbierta && (
        <Card data-testid="data-panel-ficha">
          <CardHeader className="gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-xl">{fichaAbierta.nombre}</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => descargarFicha(fichaAbierta.url, fichaAbierta.nombre)}><Download className="h-4 w-4 mr-2" /> Descargar</Button>
                <Button variant="outline" size="sm" onClick={() => imprimirFicha(fichaAbierta.url)}><Printer className="h-4 w-4 mr-2" /> Imprimir</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-200 rounded-md p-4 sm:p-8 flex justify-center">
              <img
                src={fichaAbierta.img}
                alt={fichaAbierta.nombre}
                className="w-full max-w-[950px] h-auto bg-white rounded-md shadow-lg"
                data-testid="data-ficha-img"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Panel expandible: Ficha subida */}
      {(() => {
        const up = uploaded.find((f) => f.id === abierto);
        if (!up) return null;
        return (
          <Card data-testid="data-panel-ficha-up">
            <CardHeader className="gap-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-xl">{up.tipo_documento}</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => blobFicha(up, true)}><Download className="h-4 w-4 mr-2" /> Descargar</Button>
                  <Button variant="outline" size="sm" onClick={() => blobFicha(up, false)}><Printer className="h-4 w-4 mr-2" /> Imprimir</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-200 rounded-md p-4 sm:p-8 flex justify-center">
                {prevUrls[up.id] ? (
                  <img src={prevUrls[up.id]} alt={up.tipo_documento} className="w-full max-w-[950px] h-auto bg-white rounded-md shadow-lg" data-testid="data-ficha-up-img" />
                ) : <div className="h-40 flex items-center justify-center text-muted-foreground">Cargando vista previa...</div>}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Diálogo: subir nueva ficha */}
      <Dialog open={upOpen} onOpenChange={setUpOpen}>
        <DialogContent className="max-w-md" data-testid="data-subir-ficha-dialog">
          <DialogHeader>
            <DialogTitle>Subir nueva ficha técnica</DialogTitle>
            <DialogDescription>Archivo PDF, máximo 15 MB.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="up-nombre">Nombre de la ficha</Label>
              <Input id="up-nombre" value={upNombre} onChange={(e) => setUpNombre(e.target.value)} placeholder="Ej. Modelo Media Caña" data-testid="up-nombre-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="up-file">Archivo (PDF)</Label>
              <Input id="up-file" type="file" accept="application/pdf" ref={upFileRef} data-testid="up-file-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpOpen(false)}>Cancelar</Button>
            <Button onClick={subirFicha} disabled={uploading} data-testid="up-guardar">{uploading ? "Subiendo..." : "Subir ficha"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
